import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { asignarTarifa, gruposDe, motivoNoAsignar } from "../../domain/mapa/asignar";
import { esParte, partesDe, separarParte } from "../../domain/mapa/partes";
import { motivoNoBorrarSector, resumen } from "../../domain/mapa/seleccion";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  Slider,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, ChevronLeft, Colorize, FormatAlignCenter, ContentCopy, DeleteOutline, ExpandMore, FlipToFront, FlipToBack } from "@mui/icons-material";
import {
  aplicarFilas,
  configFilas,
  agregarMesa,
  actualizarMesa,
  duplicarMesa,
  quitarMesa,
  MAX_FILAS,
  MAX_ASIENTOS_POR_FILA,
  MAX_SILLAS_MESA,
  quitarFilas,
  nombreFila,
  ajustarCaja,
  ajustarFila,
  alinearFila,
  indiceEnBloque,
  separarFilas,
  cambiarTipo,
  tipoDe,
  TIPO_UBICACION,
} from "../../domain/mapa/generador";
import { RADIO_ASIENTO } from "../../domain/mapa/geometria";
import { borrarVarios, cambiarSector } from "./acciones";
import useEditorMapa, { useMapaActual, useTarifas } from "../../stores/useEditorMapa";
import {
  COLORES,
  actualizarSector,
  quitarSector,
  duplicarSector,
  actualizarDecorativo,
  quitarDecorativo,
  conLugares,
  capacidadSector,
  capacidadTotal,
  coloresDeTarifas,
  colorDeUbicacion,
  sePuedeBorrar,
  moverTarifa,
  motivoNoMover,
} from "../../domain/mapa/mapa";
import { formatoPrecio } from "./PanelTarifas";
import { BotonConfigurarMesa } from "./ConfigurarMesa";
import { COLOR, ANCHO_PANEL_DER, FUENTE } from "./estilos";
import { estiloInput, estiloSwitch } from "../ui/Formulario";

// Seccion: bloque del panel; con título se puede plegar
const Seccion = ({ titulo, children, plegada = false }) => {
  const [abierta, setAbierta] = useState(!plegada);
  return (
    <Box sx={{ borderBottom: `1px solid ${COLOR.borde}` }}>
      {titulo && (
        <ButtonBase
          onClick={() => setAbierta((v) => !v)}
          sx={{ width: "100%", justifyContent: "space-between", px: 2.5, pt: 2, pb: abierta ? 1.25 : 2 }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: COLOR.textoSuave, textTransform: "uppercase" }}>
            {titulo}
          </Typography>
          <ExpandMore sx={{ fontSize: 18, color: COLOR.textoSuave, transform: abierta ? "none" : "rotate(-90deg)", transition: "transform .15s" }} />
        </ButtonBase>
      )}
      {abierta && (
        <Box sx={{ px: 2.5, pb: 2, pt: titulo ? 0 : 2, display: "flex", flexDirection: "column", gap: 1.5 }}>{children}</Box>
      )}
    </Box>
  );
};

// el color de una ubicación es el de su tarifa (gris si no tiene)
const useColorUbicacion = (sector) => {
  const tarifas = useTarifas();
  return colorDeUbicacion(sector, coloresDeTarifas(tarifas));
};

const Etiqueta = ({ children }) => <Typography sx={{ fontSize: 12, color: COLOR.textoSuave, mb: 0.5 }}>{children}</Typography>;

// CampoTexto: etiqueta arriba, como el resto de los formularios de PassGo
const CampoTexto = ({ etiqueta, ...props }) => (
  <Box sx={{ minWidth: 0 }}>
    {etiqueta && <Etiqueta>{etiqueta}</Etiqueta>}
    <TextField size="small" fullWidth sx={estiloInput} {...props} />
  </Box>
);

// Numero deja escribir libre (borrar todo para poner un 3) y aplica el valor
// en cuanto es válido. Al salir, si quedó vacío o fuera de rango, se ajusta.
const Numero = ({ etiqueta, valor, onCambiar, min = 0, max = Infinity, sufijo, deshabilitado }) => {
  const [texto, setTexto] = useState(String(valor ?? ""));
  const [enfocado, setEnfocado] = useState(false);

  useEffect(() => {
    if (!enfocado) setTexto(String(valor ?? ""));
  }, [valor, enfocado]);

  const ajustar = (n) => Math.min(max, Math.max(min, Math.round(n)));

  const alCambiar = (e) => {
    const nuevoTexto = e.target.value;
    setTexto(nuevoTexto);
    const n = Number(nuevoTexto);
    if (nuevoTexto.trim() !== "" && Number.isFinite(n) && n >= min && n <= max) onCambiar(Math.round(n));
  };

  const alSalir = () => {
    setEnfocado(false);
    const n = Number(texto);
    let final;
    if (texto.trim() === "" || !Number.isFinite(n)) {
      final = min >= 0 ? min : valor ?? 0;
    } else {
      final = ajustar(n);
    }
    setTexto(String(final));
    if (final !== valor) onCambiar(final);
  };

  return (
    <CampoTexto
      etiqueta={etiqueta}
      type="number"
      value={texto}
      disabled={deshabilitado}
      inputProps={{ min, max: Number.isFinite(max) ? max : undefined }}
      onFocus={() => setEnfocado(true)}
      onChange={alCambiar}
      onBlur={alSalir}
      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      InputProps={sufijo ? { endAdornment: <Typography sx={{ fontSize: 12, color: COLOR.textoSuave }}>{sufijo}</Typography> } : undefined}
    />
  );
};

const Interruptor = ({ etiqueta, valor, onCambiar }) => (
  <FormControlLabel
    sx={{ ml: -0.5, my: -0.5 }}
    control={<Switch size="small" sx={estiloSwitch} checked={valor} onChange={(e) => onCambiar(e.target.checked)} />}
    label={<Typography sx={{ fontSize: 14, color: COLOR.texto }}>{etiqueta}</Typography>}
  />
);

const Par = ({ children }) => <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>{children}</Box>;

// Encabezado del elemento elegido: volver, color, nombre y un resumen
const Encabezado = ({ color, titulo, detalle, volver, onVolver }) => (
  <Box sx={{ px: 2.5, pt: 1.5, pb: 2, borderBottom: `1px solid ${COLOR.borde}` }}>
    <ButtonBase onClick={onVolver} sx={{ gap: 0.5, color: COLOR.textoSuave, fontSize: 13, mb: 1.25, "&:hover": { color: COLOR.titulo } }}>
      <ChevronLeft sx={{ fontSize: 18 }} />
      {volver}
    </ButtonBase>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      {color && <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />}
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 17, fontWeight: 700, color: COLOR.titulo, fontFamily: FUENTE }}>{titulo}</Typography>
        {detalle && <Typography noWrap sx={{ fontSize: 13, color: COLOR.textoSuave }}>{detalle}</Typography>}
      </Box>
    </Box>
  </Box>
);

const botonTexto = { justifyContent: "flex-start", textTransform: "none", alignSelf: "flex-start", px: 1, color: COLOR.acento };

// SelectorColor: los colores de PassGo y, al final, uno a elección (con el
// gotero; muestra el color elegido cuando no es uno de la lista)
const SelectorColor = ({ valor, onCambiar }) => {
  const propio = valor && !COLORES.includes(valor);
  const circulo = (seleccionado) => ({
    width: 22,
    height: 22,
    borderRadius: "50%",
    outline: seleccionado ? `2px solid ${COLOR.titulo}` : "none",
    outlineOffset: 2,
  });
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
      {COLORES.map((c) => (
        <ButtonBase key={c} onClick={() => onCambiar(c)} aria-label={`Color ${c}`} sx={{ ...circulo(valor === c), bgcolor: c }} />
      ))}
      <Tooltip title="Elegir otro color">
        <Box
          component="label"
          sx={{
            ...circulo(propio),
            m: 0,
            position: "relative",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: propio ? valor : "conic-gradient(#e5484d, #ff8a00, #d4a017, #30a46c, #0091ff, #5656ff, #8e4ec6, #e5484d)",
          }}
        >
          <Colorize sx={{ fontSize: 13, color: "#fff", filter: "drop-shadow(0 0 1px rgba(0,0,0,.6))" }} />
          <Box
            component="input"
            type="color"
            value={valor || "#5656ff"}
            onChange={(e) => onCambiar(e.target.value)}
            sx={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

const ListaUbicaciones = ({ sectores }) => {
  const mapa = useMapaActual();
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const tarifas = useTarifas();
  const colores = coloresDeTarifas(tarifas);
  return (
    <Box sx={{ mx: -1 }}>
      {sectores.map((s) => (
        <ButtonBase
          key={s.clave}
          onClick={() => seleccionar({ tipo: "sector", clave: s.clave })}
          sx={{ justifyContent: "space-between", width: "100%", px: 1, py: 0.75, borderRadius: "6px", "&:hover": { bgcolor: "#f4f4f5" } }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colorDeUbicacion(s, colores), flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: 14, color: COLOR.titulo }}>{s.nombre}</Typography>
            {partesDe(mapa, s.clave).length > 0 && (
              <Typography sx={{ fontSize: 12, color: COLOR.textoSuave, flexShrink: 0 }}>
                {partesDe(mapa, s.clave).length + 1} partes
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 13, color: COLOR.textoSuave, flexShrink: 0 }}>
            {[s, ...partesDe(mapa, s.clave)].reduce((t, x) => t + capacidadSector(x), 0)}
          </Typography>
        </ButtonBase>
      ))}
    </Box>
  );
};

// PropiedadesPlano: la vista general del lugar cuando no hay nada elegido
const PropiedadesPlano = () => {
  const mapa = useMapaActual();
  // las partes se cuentan dentro de su ubicación
  const enMapa = mapa.sectores.filter((s) => s.en_mapa && !esParte(s));
  const fuera = mapa.sectores.filter((s) => !s.en_mapa && !esParte(s));

  return (
    <>
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${COLOR.borde}` }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: COLOR.titulo, fontFamily: FUENTE }}>Vista general</Typography>
        <Typography sx={{ fontSize: 12, color: COLOR.textoSuave, mt: 1.5 }}>Capacidad total</Typography>
        <Typography sx={{ fontSize: 30, fontWeight: 700, color: COLOR.titulo, lineHeight: 1.1, fontFamily: FUENTE }}>
          {capacidadTotal(mapa)}
        </Typography>
      </Box>
      <Seccion titulo={`Ubicaciones · ${enMapa.length}`}>
        {enMapa.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>Agregá una ubicación desde el panel de la izquierda.</Typography>
        ) : (
          <ListaUbicaciones sectores={enMapa} />
        )}
      </Seccion>
      {fuera.length > 0 && (
        <Seccion titulo={`Fuera del mapa · ${fuera.length}`}>
          <Typography sx={{ fontSize: 12, color: COLOR.textoSuave }}>
            Se venden por cantidad, sin elegir lugar. Para que el comprador elija su lugar, cambiá su tipo a filas o mesas.
          </Typography>
          <ListaUbicaciones sectores={fuera} />
        </Seccion>
      )}
    </>
  );
};

const SeccionFilas = ({ sector }) => {
  const cambiar = useEditorMapa((s) => s.cambiar);
  const config = configFilas(sector);
  const separacionMinima = 2 * RADIO_ASIENTO + 2;
  const aplicarCambios = (cambios, clave) =>
    cambiarSector(cambiar, sector.clave, (s) => aplicarFilas(s, { ...configFilas(s), ...cambios }), {
      agrupar: `${sector.clave}-filas-${clave}`,
    });
  const aplicar = (campo) => (valor) => aplicarCambios({ [campo]: valor }, campo);

  return (
    <>
    <Seccion titulo="Distribución">
      {!sector.editor?.filas && (
        <Alert severity="info" sx={{ py: 0, fontSize: 12 }}>
          Esta ubicación se armó con el editor anterior: si cambiás algo, sus filas se vuelven a armar.
        </Alert>
      )}
      <Par>
        <Numero etiqueta="Filas" valor={config.filas} min={1} max={MAX_FILAS} onCambiar={aplicar("filas")} />
        <Numero etiqueta="Asientos por fila" valor={config.asientosPorFila} min={1} max={MAX_ASIENTOS_POR_FILA} onCambiar={aplicar("asientosPorFila")} />
      </Par>
      <Par>
        <Numero etiqueta="Entre asientos" valor={config.separacion} min={separacionMinima} max={200} onCambiar={aplicar("separacion")} sufijo="px" />
        <Numero etiqueta="Entre filas" valor={config.separacionFilas} min={separacionMinima} max={300} onCambiar={aplicar("separacionFilas")} sufijo="px" />
      </Par>
      <Box>
        <Etiqueta>Curva</Etiqueta>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pl: 1 }}>
          <Slider size="small" value={config.curva} min={0} max={100} onChange={(_, v) => aplicar("curva")(v)} sx={{ color: COLOR.acento, flex: 1 }} />
          <Box sx={{ width: 76, flexShrink: 0 }}>
            <Numero valor={config.curva} min={0} max={100} onCambiar={aplicar("curva")} />
          </Box>
        </Box>
      </Box>
    </Seccion>
    <Seccion titulo="Numeración" plegada>
      <CampoTexto
        select
        etiqueta="Mostrar en el mapa"
        value={sector.editor?.numeracion || "todo"}
        onChange={(e) =>
          cambiarSector(cambiar, sector.clave, (s) =>
            ajustarCaja({ ...s, editor: { ...(s.editor || {}), numeracion: e.target.value === "todo" ? undefined : e.target.value } })
          )
        }
        helperText="Los tickets llevan fila y asiento igual."
      >
        <MenuItem value="todo">Filas y números de asiento</MenuItem>
        <MenuItem value="filas">Solo el nombre de las filas</MenuItem>
        <MenuItem value="nada">Sin numeración</MenuItem>
      </CampoTexto>
      <Par>
        <CampoTexto
          select
          etiqueta="Filas"
          value={config.nombres}
          onChange={(e) => aplicarCambios({ nombres: e.target.value, primeraFila: e.target.value === "numeros" ? "1" : "A" }, "nombres")}
        >
          <MenuItem value="letras">A, B, C…</MenuItem>
          <MenuItem value="numeros">1, 2, 3…</MenuItem>
        </CampoTexto>
        <CampoTexto
          etiqueta="Primera fila"
          value={config.primeraFila}
          onChange={(e) => aplicar("primeraFila")(e.target.value.trim() || (config.nombres === "numeros" ? "1" : "A"))}
        />
      </Par>
      <Par>
        <Numero etiqueta="Primer asiento" valor={config.primerAsiento} min={1} max={9999} onCambiar={aplicar("primerAsiento")} />
        <CampoTexto select etiqueta="Sentido" value={config.sentido} onChange={(e) => aplicar("sentido")(e.target.value)}>
          <MenuItem value="izquierda">Izq. → der.</MenuItem>
          <MenuItem value="derecha">Der. → izq.</MenuItem>
        </CampoTexto>
      </Par>
      <Typography sx={{ fontSize: 12, color: COLOR.textoSuave, bgcolor: "#f4f4f5", borderRadius: "8px", px: 1.5, py: 1 }}>
        Filas {nombreFila(0, config)} a {nombreFila(config.filas - 1, config)} · asientos {Number(config.primerAsiento) || 1} a{" "}
        {(Number(config.primerAsiento) || 1) + config.asientosPorFila - 1}, de {config.sentido === "derecha" ? "derecha a izquierda" : "izquierda a derecha"}
      </Typography>
    </Seccion>
    {sector.mesas.length > 0 && (
      <Seccion>
        <Button color="error" startIcon={<DeleteOutline />} onClick={() => cambiarSector(cambiar, sector.clave, quitarFilas)} sx={botonTexto}>
          Quitar las filas
        </Button>
      </Seccion>
    )}
    </>
  );
};

const SeccionMesas = ({ sector }) => {
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  return (
    <Seccion titulo={`Mesas (${sector.mesas.length})`}>
      <Box>
        {sector.mesas.map((m) => (
          <ButtonBase
            key={m.clave}
            onClick={() => seleccionar({ tipo: "mesa", sector: sector.clave, clave: m.clave })}
            sx={{ justifyContent: "space-between", width: "100%", px: 1, py: 0.75, borderRadius: "6px", "&:hover": { bgcolor: COLOR.acentoSuave } }}
          >
            <Typography noWrap sx={{ fontSize: 14 }}>{m.nombre}</Typography>
            <Typography sx={{ fontSize: 12, color: COLOR.textoSuave, flexShrink: 0 }}>
              {m.vendida ? "vendida · " : ""}
              {m.sillas.length} sillas
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Seccion>
  );
};

// AgregarLugares suma más filas o mesas a una ubicación que ya tiene lugares
const AgregarLugares = ({ sector }) => {
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const mesa = (forma) => {
    const r = cambiarSector(cambiar, sector.clave, (s) => agregarMesa(s, { forma, sillas: forma === "rectangular" ? 8 : 6 }));
    if (r?.clave) seleccionar({ tipo: "mesa", sector: sector.clave, clave: r.clave });
  };
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mx: -1 }}>
      {sector.filas.length === 0 && (
        <Button size="small" startIcon={<Add />} onClick={() => cambiarSector(cambiar, sector.clave, (s) => aplicarFilas(s, configFilas(s)))} sx={botonTexto}>
          Filas
        </Button>
      )}
      <Button size="small" startIcon={<Add />} onClick={() => mesa("redonda")} sx={botonTexto}>
        Mesa redonda
      </Button>
      <Button size="small" startIcon={<Add />} onClick={() => mesa("rectangular")} sx={botonTexto}>
        Mesa rectangular
      </Button>
    </Box>
  );
};

const PropiedadesMesa = ({ sector, mesa }) => {
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const editar = (campo) => (valor) =>
    cambiarSector(cambiar, sector.clave, (s) => actualizarMesa(s, mesa.clave, { [campo]: valor }), {
      agrupar: `${mesa.clave}-${campo}`,
    });
  const tieneVentas = mesa.vendida || mesa.sillas.some((s) => s.vendido);

  return (
    <>
      <Encabezado
        color={useColorUbicacion(sector)}
        titulo={mesa.nombre || "Mesa"}
        detalle={`${mesa.forma === "rectangular" ? "Mesa rectangular" : "Mesa redonda"} · ${mesa.sillas.length} sillas${tieneVentas ? (mesa.vendida ? " · vendida entera" : " · con sillas vendidas") : ""}`}
        volver={sector.nombre}
        onVolver={() => seleccionar({ tipo: "sector", clave: sector.clave })}
      />
      <Seccion titulo="Mesa">
        <CampoTexto etiqueta="Nombre" value={mesa.nombre} onChange={(e) => editar("nombre")(e.target.value)} />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mesa.forma}
          onChange={(_, v) => v && editar("forma")(v)}
          fullWidth
          sx={{ "& .MuiToggleButton-root": { textTransform: "none" } }}
        >
          <ToggleButton value="redonda">Redonda</ToggleButton>
          <ToggleButton value="rectangular">Rectangular</ToggleButton>
        </ToggleButtonGroup>
        <Par>
          <Numero etiqueta="Sillas" valor={mesa.sillas.length} min={1} max={MAX_SILLAS_MESA} onCambiar={editar("sillas")} deshabilitado={mesa.vendida} />
          <Numero etiqueta="Rotación" valor={mesa.rotacion} min={-360} max={360} onCambiar={editar("rotacion")} sufijo="°" />
        </Par>
      </Seccion>
      <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        <Button
          startIcon={<ContentCopy />}
          onClick={() => {
            const r = cambiarSector(cambiar, sector.clave, (s) => duplicarMesa(s, mesa.clave));
            if (r?.clave) seleccionar({ tipo: "mesa", sector: sector.clave, clave: r.clave });
          }}
          sx={{ ...botonTexto, color: COLOR.texto }}
        >
          Duplicar mesa
        </Button>
        <Tooltip title={tieneVentas ? "Tiene sillas vendidas" : ""}>
          <span>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              disabled={tieneVentas}
              onClick={() => {
                if (cambiarSector(cambiar, sector.clave, (s) => quitarMesa(s, mesa.clave))) {
                  seleccionar({ tipo: "sector", clave: sector.clave });
                }
              }}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              Eliminar mesa
            </Button>
          </span>
        </Tooltip>
      </Box>
    </>
  );
};

// TarifasDelSector: las entradas que se venden en el sector y un selector
// para traer otra (se saca de donde estaba: una tarifa va en un solo sector).
const TarifasDelSector = ({ sector }) => {
  const mapa = useMapaActual();
  const tarifas = useTarifas();
  const cambiar = useEditorMapa((s) => s.cambiar);
  const propias = tarifas.filter((t) => sector.tarifas.includes(t.id));
  const otras = tarifas.filter((t) => !sector.tarifas.includes(t.id));

  return (
    <Seccion titulo={`Tarifas (${propias.length})`}>
      {propias.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>Todavía no se vende ninguna entrada en esta ubicación.</Typography>
      ) : (
        <Box>
          {propias.map((t) => (
            <Box key={t.id} sx={{ py: 0.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Typography noWrap sx={{ fontSize: 14 }} title={t.concepto}>
                  {t.concepto}
                </Typography>
                <Typography sx={{ fontSize: 13, color: COLOR.textoSuave, flexShrink: 0 }}>{formatoPrecio(t.precio)}</Typography>
              </Box>
              <BotonConfigurarMesa tarifa={t} sector={sector} />
            </Box>
          ))}
          <Typography sx={{ fontSize: 11, color: COLOR.textoSuave, mt: 0.5 }}>
            Para sacarla, asignala a otra ubicación.
          </Typography>
        </Box>
      )}
      {otras.length > 0 && (
        <CampoTexto
          select
          etiqueta="Vender acá otra entrada"
          value=""
          onChange={(e) => cambiar((m) => moverTarifa(m, Number(e.target.value), sector.clave))}
        >
          {otras.map((t) => {
            const motivo = motivoNoMover(mapa, t, sector);
            const donde = mapa.sectores.find((s) => s.tarifas.includes(t.id));
            return (
              <MenuItem key={t.id} value={t.id} disabled={Boolean(motivo)}>
                <Box>
                  <Typography sx={{ fontSize: 14 }}>{t.concepto}</Typography>
                  <Typography sx={{ fontSize: 11, color: COLOR.textoSuave }}>
                    {motivo || (donde ? `Hoy en ${donde.nombre}` : "Sin ubicación")}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </CampoTexto>
      )}
    </Seccion>
  );
};

const PanelTarifasPlano = () => {
  const mapa = useMapaActual();
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  return (
    <Seccion titulo="Ubicaciones">
      <Typography sx={{ fontSize: 12, color: COLOR.textoSuave }}>
        Elegí una ubicación en el plano o en la lista para ver y asignar sus entradas.
      </Typography>
      <Box>
        {mapa.sectores.filter((s) => !esParte(s)).map((s) => (
          <ButtonBase
            key={s.clave}
            onClick={() => seleccionar({ tipo: "sector", clave: s.clave })}
            sx={{ justifyContent: "space-between", width: "100%", px: 1, py: 0.75, borderRadius: "6px", "&:hover": { bgcolor: COLOR.acentoSuave } }}
          >
            <Typography noWrap sx={{ fontSize: 14 }}>{s.nombre}</Typography>
            <Typography sx={{ fontSize: 12, flexShrink: 0, color: s.tarifas.length === 0 ? "#b45309" : COLOR.textoSuave }}>
              {s.tarifas.length === 0 ? "Sin tarifas" : `${s.tarifas.length} tarifa${s.tarifas.length === 1 ? "" : "s"}`}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Seccion>
  );
};

const DETALLE_TIPO = {
  [TIPO_UBICACION.GENERAL]: "General",
  [TIPO_UBICACION.FILAS]: "Filas de asientos",
  [TIPO_UBICACION.MESAS]: "Mesas",
  [TIPO_UBICACION.MIXTA]: "Filas y mesas",
};

const PropiedadesSector = ({ sector }) => {
  const mapa = useMapaActual();
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const lugares = conLugares(sector);
  const tipo = tipoDe(sector);
  const principal = esParte(sector) ? mapa.sectores.find((s) => s.clave === sector.editor.parteDe) : null;
  // el nombre y el límite son de la ubicación: en una parte se editan en la principal
  const editar = (campo) => (valor) => {
    const clave = principal && (campo === "nombre" || campo === "limite_persona") ? principal.clave : sector.clave;
    cambiar((m) => actualizarSector(m, clave, { [campo]: valor }), { agrupar: `${clave}-${campo}` });
  };

  const motivoNoBorrar = !sePuedeBorrar(sector)
    ? "Tiene entradas vendidas"
    : sector.tarifas.length > 0 && !principal
      ? "Tiene tarifas asignadas: cambialas de ubicación primero"
      : null;
  const partes = principal ? [] : partesDe(mapa, sector.clave);

  return (
    <>
      <Encabezado
        color={useColorUbicacion(sector)}
        titulo={sector.nombre || "Sin nombre"}
        detalle={
          principal
            ? `Parte de ${principal.nombre} · ${capacidadSector(sector)} lugares`
            : `${DETALLE_TIPO[tipo]} · ${capacidadSector(sector)} lugares${partes.length ? ` · ${partes.length + 1} partes` : ""}${sector.con_ventas ? " · con ventas" : ""}`
        }
        volver="Vista general"
        onVolver={() => seleccionar(null)}
      />
      {principal && (
        <Seccion>
          <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>
            Es una parte de {principal.nombre} en otro lugar del mapa: se vende con sus mismas entradas y comparte el cupo.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button size="small" onClick={() => seleccionar({ tipo: "sector", clave: principal.clave })} sx={{ ...botonTexto, color: COLOR.acento }}>
              Ver {principal.nombre}
            </Button>
            <Button
              size="small"
              onClick={() => cambiar((m) => separarParte(m, sector.clave))}
              sx={{ ...botonTexto, color: COLOR.texto }}
            >
              Hacerla una ubicación aparte
            </Button>
          </Box>
        </Seccion>
      )}
      <Seccion titulo="Ubicación">
        <CampoTexto etiqueta="Nombre" value={sector.nombre} onChange={(e) => editar("nombre")(e.target.value)} />
        <CampoTexto
          etiqueta="Tipo de ubicación"
          select
          value={tipo}
          onChange={(e) => cambiarSector(cambiar, sector.clave, (s) => cambiarTipo(s, e.target.value))}
          helperText={tipo === TIPO_UBICACION.GENERAL ? "Se vende por cantidad, sin elegir lugar." : "El comprador elige su lugar en el mapa."}
        >
          <MenuItem value={TIPO_UBICACION.GENERAL}>General (sin lugares)</MenuItem>
          <MenuItem value={TIPO_UBICACION.FILAS}>Filas de asientos</MenuItem>
          <MenuItem value={TIPO_UBICACION.MESAS}>Mesas</MenuItem>
          {tipo === TIPO_UBICACION.MIXTA && <MenuItem value={TIPO_UBICACION.MIXTA}>Filas y mesas</MenuItem>}
        </CampoTexto>
        {!lugares && (
          <Interruptor
            etiqueta="Mostrar en el plano"
            valor={sector.en_mapa}
            onCambiar={(v) => cambiar((m) => actualizarSector(m, sector.clave, { en_mapa: v }))}
          />
        )}
        {sector.en_mapa && (
          <>
            <Interruptor
              etiqueta="Mostrar el nombre"
              valor={!sector.editor?.ocultarNombre}
              onCambiar={(v) => editar("editor")({ ...(sector.editor || {}), ocultarNombre: v ? undefined : true })}
            />
            <Interruptor
              etiqueta="Mostrar fondo y borde"
              valor={!sector.editor?.ocultarFondo}
              onCambiar={(v) => editar("editor")({ ...(sector.editor || {}), ocultarFondo: v ? undefined : true })}
            />
          </>
        )}
      </Seccion>
      <Seccion titulo="Capacidad y venta">
        <Par>
          {lugares ? (
            <Box>
              <Etiqueta>Capacidad</Etiqueta>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: COLOR.titulo, py: 0.9 }}>{capacidadSector(sector)} lugares</Typography>
            </Box>
          ) : (
            <Numero etiqueta="Capacidad" valor={sector.capacidad} min={0} onCambiar={editar("capacidad")} />
          )}
          <Numero etiqueta="Máx. por comprador" valor={sector.limite_persona} min={1} onCambiar={editar("limite_persona")} />
        </Par>
      </Seccion>
      {sector.filas.length > 0 && <SeccionFilas sector={sector} />}
      {sector.mesas.length > 0 && <SeccionMesas sector={sector} />}
      {lugares && (
        <Seccion titulo="Agregar">
          <AgregarLugares sector={sector} />
        </Seccion>
      )}
      {!principal && <TarifasDelSector sector={sector} />}
      {sector.en_mapa && (
        <Seccion titulo="Posición" plegada>
          <Par>
            <Numero etiqueta="X" valor={sector.x} min={-100000} onCambiar={editar("x")} />
            <Numero etiqueta="Y" valor={sector.y} min={-100000} onCambiar={editar("y")} />
          </Par>
          {!lugares && (
            <Par>
              <Numero etiqueta="Ancho" valor={sector.ancho} min={40} onCambiar={editar("ancho")} />
              <Numero etiqueta="Alto" valor={sector.alto} min={30} onCambiar={editar("alto")} />
            </Par>
          )}
          <Numero etiqueta="Rotación" valor={sector.rotacion} min={-360} onCambiar={editar("rotacion")} sufijo="°" />
        </Seccion>
      )}
      <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        <Button
          startIcon={<ContentCopy />}
          onClick={() => {
            let clave = null;
            cambiar((m) => {
              const r = duplicarSector(m, sector.clave);
              clave = r.clave;
              return r.mapa;
            });
            if (clave) seleccionar({ tipo: "sector", clave });
          }}
          sx={{ ...botonTexto, color: COLOR.texto }}
        >
          Duplicar ubicación
        </Button>
        <Tooltip title={motivoNoBorrar || ""}>
          <span>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              disabled={Boolean(motivoNoBorrar)}
              onClick={() => {
                cambiar((m) => quitarSector(m, sector.clave));
                seleccionar(null);
              }}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              {principal ? "Eliminar esta parte" : "Eliminar ubicación"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </>
  );
};

// PropiedadesFila: una fila sola, para moverla o girarla aparte del bloque
const PropiedadesFila = ({ sector, indice }) => {
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const fila = sector.filas[indice];
  const ajuste = configFilas(sector).ajustes?.[indiceEnBloque(sector, indice)];
  return (
    <>
      <Encabezado
        color={useColorUbicacion(sector)}
        titulo={fila.nombre ? `Fila ${fila.nombre}` : `Fila ${indice + 1}`}
        detalle={`${fila.asientos.length} asientos · ${sector.nombre}`}
        volver={sector.nombre}
        onVolver={() => seleccionar({ tipo: "sector", clave: sector.clave })}
      />
      <Seccion titulo="Fila">
        <Par>
          <CampoTexto
            etiqueta="Nombre"
            value={fila.nombre}
            onChange={(e) =>
              cambiarSector(cambiar, sector.clave, (s) => ajustarFila(s, indice, { nombre: e.target.value }), {
                agrupar: `${sector.clave}-fila-${indice}-nombre`,
              })
            }
          />
          <Numero
            etiqueta="Asientos"
            valor={fila.asientos.length}
            min={1}
            max={MAX_ASIENTOS_POR_FILA}
            onCambiar={(v) => cambiarSector(cambiar, sector.clave, (s) => ajustarFila(s, indice, { asientos: v }), { agrupar: `${sector.clave}-fila-${indice}-asientos` })}
          />
        </Par>
        <Typography sx={{ fontSize: 12, color: COLOR.textoSuave }}>
          Arrastrá la fila para moverla, o los "+" de las puntas para sumarle o sacarle asientos.
        </Typography>
        {Boolean(ajuste?.dx || ajuste?.dy) && (
          <Button startIcon={<FormatAlignCenter />} onClick={() => cambiarSector(cambiar, sector.clave, (s) => alinearFila(s, indice))} sx={botonTexto}>
            Volver a alinear con las demás
          </Button>
        )}
      </Seccion>
    </>
  );
};

// VenderLoElegido: asigna una tarifa a las filas o mesas elegidas (con la misma
// regla que "Asignar" de la lista de la izquierda)
const VenderLoElegido = () => {
  const mapa = useMapaActual();
  const tarifas = useTarifas();
  const seleccion = useEditorMapa((s) => s.seleccion);
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);

  const asignar = (idtarifa) => {
    let resultado = null;
    cambiar((m) => {
      resultado = asignarTarifa(m, seleccion, idtarifa);
      return resultado.error ? m : resultado.mapa;
    });
    if (resultado?.error) toast.error(resultado.error);
    else if (resultado?.clave) seleccionar({ tipo: "sector", clave: resultado.clave });
  };

  return (
    <Seccion titulo="Vender lo elegido con">
      <CampoTexto
        select
        etiqueta="Entrada"
        value=""
        onChange={(e) => asignar(Number(e.target.value))}
        helperText="Si esa entrada ya se vende en otra ubicación, lo elegido pasa a ser parte de ella (mismo cupo), sin moverse de lugar. Si no se vende en ningún lado, pasa a ser su propia ubicación. Con Shift sumás más."
      >
        {tarifas.map((t) => {
          const motivo = motivoNoAsignar(mapa, t, seleccion);
          return (
            <MenuItem key={t.id} value={t.id} disabled={Boolean(motivo)}>
              <Box>
                <Typography sx={{ fontSize: 14 }}>{t.concepto} · {formatoPrecio(t.precio)}</Typography>
                {motivo && <Typography sx={{ fontSize: 11, color: COLOR.textoSuave }}>{motivo}</Typography>}
              </Box>
            </MenuItem>
          );
        })}
      </CampoTexto>
    </Seccion>
  );
};

// TarifasDeLaFila: en "Asignar tarifas", lo que se vende en la fila y el
// selector para venderla con otra
const TarifasDeLaFila = ({ sector, indices }) => {
  const tarifas = useTarifas();
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const filas = indices.map((k) => sector.filas[k]).filter(Boolean);
  const propias = tarifas.filter((t) => sector.tarifas.includes(t.id));
  const asientos = filas.reduce((t, f) => t + f.asientos.length, 0);
  const seleccion = useEditorMapa((s) => s.seleccion);
  const total = seleccion?.elementos?.length || filas.length;
  const ubicaciones = new Set((seleccion?.elementos || []).map((e) => e.sector)).size;
  const titulo =
    total > filas.length
      ? `${total} filas`
      : filas.length === 1
        ? `Fila ${filas[0].nombre}`
        : `${filas.length} filas (${filas.map((f) => f.nombre).join(", ")})`;

  return (
    <>
      <Encabezado
        color={useColorUbicacion(sector)}
        titulo={titulo}
        detalle={ubicaciones > 1 ? `En ${ubicaciones} ubicaciones` : `${asientos} asientos · ${sector.nombre}`}
        volver={sector.nombre}
        onVolver={() => seleccionar({ tipo: "sector", clave: sector.clave })}
      />
      <Seccion titulo="Se vende con">
        {propias.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>La ubicación todavía no tiene tarifas.</Typography>
        ) : (
          propias.map((t) => (
            <Box key={t.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography noWrap sx={{ fontSize: 14 }}>{t.concepto}</Typography>
              <Typography sx={{ fontSize: 13, color: COLOR.textoSuave, flexShrink: 0 }}>{formatoPrecio(t.precio)}</Typography>
            </Box>
          ))
        )}
        <Typography sx={{ fontSize: 12, color: COLOR.textoSuave }}>Las mismas que el resto de {sector.nombre}.</Typography>
      </Seccion>
      <VenderLoElegido />
    </>
  );
};

// PropiedadesVarios: varias ubicaciones u objetos elegidos en el diseño
const PropiedadesVarios = ({ elementos }) => {
  const mapa = useMapaActual();
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const tarifas = useTarifas();
  const colores = coloresDeTarifas(tarifas);
  const items = elementos
    .map((e) =>
      e.tipo === "sector"
        ? { ...e, sector: mapa.sectores.find((s) => s.clave === e.clave) }
        : { ...e, decorativo: mapa.decorativos.find((d) => d.clave === e.clave) }
    )
    .filter((e) => e.sector || e.decorativo);
  const ubicaciones = items.filter((e) => e.sector).length;
  const objetos = items.length - ubicaciones;
  const cuantos = (n, uno, varios) => (n === 1 ? `1 ${uno}` : `${n} ${varios}`);
  const detalle = [ubicaciones && cuantos(ubicaciones, "ubicación", "ubicaciones"), objetos && cuantos(objetos, "objeto", "objetos")]
    .filter(Boolean)
    .join(" y ");
  const noSeBorran = items.filter((e) => e.sector && motivoNoBorrarSector(e.sector)).length;

  return (
    <>
      <Encabezado titulo={`${items.length} elegidos`} detalle={detalle} volver="Vista general" onVolver={() => seleccionar(null)} />
      <Seccion>
        <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>
          Arrastrá cualquiera para moverlos juntos, o usá las flechas. Con Shift + clic sumás o sacás.
        </Typography>
        <Box sx={{ mx: -1 }}>
          {items.map((e) => (
            <ButtonBase
              key={e.clave}
              onClick={() => seleccionar({ tipo: e.tipo, clave: e.clave })}
              sx={{ justifyContent: "flex-start", gap: 1.25, width: "100%", px: 1, py: 0.75, borderRadius: "6px", "&:hover": { bgcolor: "#f4f4f5" } }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: e.sector ? "50%" : "2px",
                  bgcolor: e.sector ? colorDeUbicacion(e.sector, colores) : e.decorativo.color || COLOR.textoSuave,
                  flexShrink: 0,
                }}
              />
              <Typography noWrap sx={{ fontSize: 14, color: COLOR.titulo }}>
                {e.sector ? e.sector.nombre : NOMBRES_OBJETO[e.decorativo.tipo] || "Objeto"}
              </Typography>
            </ButtonBase>
          ))}
        </Box>
      </Seccion>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Tooltip title={noSeBorran ? "Las ubicaciones con ventas o con tarifas no se borran" : ""}>
          <span>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              disabled={noSeBorran === items.length}
              onClick={() => borrarVarios(elementos)}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              {noSeBorran ? `Eliminar ${items.length - noSeBorran} de ${items.length}` : "Eliminar todos"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </>
  );
};

const NOMBRES_OBJETO = { texto: "Texto", rectangulo: "Rectángulo", circulo: "Círculo", linea: "Línea" };

const PropiedadesDecorativo = ({ decorativo: d }) => {
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const editar = (campo) => (valor) =>
    cambiar((m) => actualizarDecorativo(m, d.clave, { [campo]: valor }), { agrupar: `${d.clave}-${campo}` });

  return (
    <>
      <Encabezado
        color={d.color}
        titulo={NOMBRES_OBJETO[d.tipo] || "Objeto"}
        detalle={d.tipo === "texto" ? d.texto : "Objeto decorativo, no se vende"}
        volver="Vista general"
        onVolver={() => seleccionar(null)}
      />
      <Seccion titulo="Apariencia">
        {d.tipo === "texto" && (
          <>
            <CampoTexto etiqueta="Texto" value={d.texto || ""} onChange={(e) => editar("texto")(e.target.value)} />
            <Numero etiqueta="Tamaño de letra" valor={d.font_size} min={8} onCambiar={editar("font_size")} />
          </>
        )}
        <SelectorColor valor={d.color} onCambiar={(c) => cambiar((m) => actualizarDecorativo(m, d.clave, { color: c }))} />
        {(d.tipo === "rectangulo" || d.tipo === "circulo") && (
          <Par>
            <Numero etiqueta="Ancho" valor={d.ancho} min={10} onCambiar={editar("ancho")} />
            <Numero etiqueta="Alto" valor={d.alto} min={10} onCambiar={editar("alto")} />
          </Par>
        )}
        <Numero etiqueta="Rotación" valor={d.rotacion} min={-360} onCambiar={editar("rotacion")} sufijo="°" />
      </Seccion>
      <Seccion>
        <Button
          startIcon={(d.z_index || 0) >= 1 ? <FlipToBack /> : <FlipToFront />}
          onClick={() => cambiar((m) => actualizarDecorativo(m, d.clave, { z_index: (d.z_index || 0) >= 1 ? 0 : 1 }))}
          sx={{ justifyContent: "flex-start", textTransform: "none" }}
        >
          {(d.z_index || 0) >= 1 ? "Enviar detrás de las ubicaciones" : "Traer delante de las ubicaciones"}
        </Button>
        <Button
          color="error"
          startIcon={<DeleteOutline />}
          onClick={() => {
            cambiar((m) => quitarDecorativo(m, d.clave));
            seleccionar(null);
          }}
          sx={{ justifyContent: "flex-start", textTransform: "none" }}
        >
          Eliminar
        </Button>
      </Seccion>
    </>
  );
};

const PanelPropiedades = () => {
  const mapa = useMapaActual();
  const seleccion = useEditorMapa((s) => s.seleccion);
  const panelRef = useRef(null);

  // al elegir otra cosa el panel arranca desde arriba
  useEffect(() => {
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [seleccion?.tipo, seleccion?.clave]);

  const pestana = useEditorMapa((s) => s.pestana);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const tarifas = useTarifas();
  const colores = coloresDeTarifas(tarifas);

  let contenido = <PropiedadesPlano />;
  if (pestana === "tarifas" && seleccion?.tipo === "fila") {
    const sector = mapa.sectores.find((s) => s.clave === seleccion.sector);
    contenido = sector?.filas[seleccion.indice] ? (
      <TarifasDeLaFila key={`${sector.clave}-${(seleccion.indices || [seleccion.indice]).join(",")}`} sector={sector} indices={seleccion.indices || [seleccion.indice]} />
    ) : (
      <PanelTarifasPlano />
    );
  } else if (pestana === "tarifas" && seleccion?.tipo === "lugares") {
    const ubicaciones = new Set(seleccion.elementos.map((e) => e.sector)).size;
    contenido = (
      <>
        <Encabezado
          titulo={resumen(seleccion.elementos)}
          detalle={ubicaciones === 1 ? "En 1 ubicación" : `En ${ubicaciones} ubicaciones`}
          volver="Todas las ubicaciones"
          onVolver={() => seleccionar(null)}
        />
        <VenderLoElegido />
      </>
    );
  } else if (pestana === "tarifas" && seleccion?.tipo === "mesa") {
    const sector = mapa.sectores.find((s) => s.clave === seleccion.sector);
    const elegidas = sector ? sector.mesas.filter((m) => (seleccion.claves || [seleccion.clave]).includes(m.clave)) : [];
    const total = seleccion.elementos?.length || elegidas.length;
    const ubicaciones = gruposDe(seleccion).length;
    contenido = sector && elegidas.length > 0 ? (
      <>
        <Encabezado
          color={colorDeUbicacion(sector, colores)}
          titulo={total === 1 ? elegidas[0].nombre : `${total} mesas`}
          detalle={
            ubicaciones > 1 ? `En ${ubicaciones} ubicaciones` : `${elegidas.reduce((t, m) => t + m.sillas.length, 0)} sillas · ${sector.nombre}`
          }
          volver={sector.nombre}
          onVolver={() => seleccionar({ tipo: "sector", clave: sector.clave })}
        />
        <Seccion titulo="Se vende con">
          {tarifas.filter((t) => sector.tarifas.includes(t.id)).length === 0 ? (
            <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>Todavía sin entradas.</Typography>
          ) : (
            tarifas
              .filter((t) => sector.tarifas.includes(t.id))
              .map((t) => (
                <Box key={t.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography noWrap sx={{ fontSize: 14 }}>{t.concepto}</Typography>
                  <Typography sx={{ fontSize: 13, color: COLOR.textoSuave, flexShrink: 0 }}>{formatoPrecio(t.precio)}</Typography>
                </Box>
              ))
          )}
        </Seccion>
        <VenderLoElegido />
      </>
    ) : (
      <PanelTarifasPlano />
    );
  } else if (pestana === "tarifas") {
    const sector = seleccion?.tipo === "sector" ? mapa.sectores.find((s) => s.clave === seleccion.clave) : null;
    const principal = esParte(sector) ? mapa.sectores.find((s) => s.clave === sector.editor.parteDe) : null;
    contenido = sector ? (
      <>
        <Encabezado
          color={colorDeUbicacion(sector, colores)}
          titulo={sector.nombre}
          detalle={
            principal
              ? `Parte de ${principal.nombre} · ${capacidadSector(sector)} lugares`
              : `${DETALLE_TIPO[tipoDe(sector)]} · ${capacidadSector(sector)} lugares`
          }
          volver="Todas las ubicaciones"
          onVolver={() => seleccionar(null)}
        />
        {principal ? (
          <>
            <Seccion titulo="Se vende con">
              {tarifas
                .filter((t) => sector.tarifas.includes(t.id))
                .map((t) => (
                  <Box key={t.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography noWrap sx={{ fontSize: 14 }}>{t.concepto}</Typography>
                    <Typography sx={{ fontSize: 13, color: COLOR.textoSuave, flexShrink: 0 }}>{formatoPrecio(t.precio)}</Typography>
                  </Box>
                ))}
              <Typography sx={{ fontSize: 12, color: COLOR.textoSuave }}>
                Las de {principal.nombre}, con el mismo cupo.
              </Typography>
            </Seccion>
            <VenderLoElegido />
          </>
        ) : (
          <TarifasDelSector key={sector.clave} sector={sector} />
        )}
      </>
    ) : (
      <PanelTarifasPlano />
    );
  } else if (seleccion?.tipo === "varios") {
    contenido = <PropiedadesVarios elementos={seleccion.elementos} />;
  } else if (seleccion?.tipo === "sector") {
    const sector = mapa.sectores.find((s) => s.clave === seleccion.clave);
    if (sector) contenido = <PropiedadesSector key={sector.clave} sector={sector} />;
  } else if (seleccion?.tipo === "fila") {
    const sector = mapa.sectores.find((s) => s.clave === seleccion.sector);
    if (sector?.filas[seleccion.indice]) {
      contenido = <PropiedadesFila key={`${sector.clave}-${seleccion.indice}`} sector={sector} indice={seleccion.indice} />;
    }
  } else if (seleccion?.tipo === "mesa") {
    const sector = mapa.sectores.find((s) => s.clave === seleccion.sector);
    const mesa = sector?.mesas.find((m) => m.clave === seleccion.clave);
    if (mesa) contenido = <PropiedadesMesa key={mesa.clave} sector={sector} mesa={mesa} />;
  } else if (seleccion?.tipo === "decorativo") {
    const decorativo = mapa.decorativos.find((d) => d.clave === seleccion.clave);
    if (decorativo) contenido = <PropiedadesDecorativo key={decorativo.clave} decorativo={decorativo} />;
  }

  return (
    <Box
      ref={panelRef}
      sx={{
        width: ANCHO_PANEL_DER,
        flexShrink: 0,
        borderLeft: `1px solid ${COLOR.borde}`,
        bgcolor: COLOR.panel,
        overflowY: "auto",
      }}
    >
      {contenido}
    </Box>
  );
};

export default PanelPropiedades;
